import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Skeleton } from "antd";
import {
  useSearch,
  HOTELS,
  PACKAGES,
  PLANES,
  DYNAMIC_PACKAGES,
  ORGANIZED_TOURS,
  CAMPAIGNS,
} from "../../../../contexts/SearchContext";
import { useLocation, useParams } from "react-router-dom";
import SearchLoadingModal from "./SearchLoadingModal";
import { useIsMobile } from "./useIsMobile";
import MobileSearchWizard from "./MobileSearchWizard";
import MobileCategoryPicker from "./MobileCategoryPicker";
import "./SearchBlockContainer.css";
import SearchTypeBlock from "./SearchTypeBlock";
import SearchFieldsBlock from "./SearchFieldsBlock";
import QuickSearchBlock from "./QuickSearchBlock";
import "../SubComponents/SearchParamsLockButton/SearchParamsLockButton.css";
import FlightOfferCompactSummary from "./FlightOfferCompactSummary";
import {
  useFlightOfferUi,
} from "../../../../contexts/FlightOfferUiContext";

/**
 * SearchBlockContainer - Container component for modular search engine blocks
 * 
 * This component organizes the search engine into logical, reusable blocks:
 * 1. SearchTypeBlock - Tab selector for search types
 * 2. SearchFieldsBlock - Dynamic search input fields
 * 3. SearchOptionsBlock - Additional options (flexibility, filters)
 * 4. SearchButtonBlock - Main search button
 * 5. QuickSearchBlock — כפתורי קטגוריה מהירה (חבילות)
 * 
 * Benefits:
 * - Modular and maintainable
 * - Fully responsive
 * - Easy to understand and modify
 * - Visually appealing with smooth animations
 */
const SearchBlockContainer = ({
  hasResults = false,
  flightOfferCompactMode = false,
  embeddedFlightResultsEditor = false,
  onCloseSearchEditor = null,
  forceLandingLayout = false,
  loadingModalOnly = false,
}) => {
  const isMobile = useIsMobile();
  const {
    searchTab,
    setSearchTab,
    loading,
    forceStopLoading,
    searchResults,
    location,
    calendarDateArrival,
    calendarDateLeave,
    roomInfo,
    flightTripType,
  } = useSearch();
  const routerLocation = useLocation();
  const { flightStep: routeFlightStep } = useParams();
  const [flightSearchExpanded, setFlightSearchExpanded] = useState(false);
  const [embeddedEditorForceOpen, setEmbeddedEditorForceOpen] = useState(false);
  const embeddedPanelBodyRef = useRef(null);
  const { flightSearchEditorOpen, markFlightEditorSearchSubmittedFromResultsBar } =
    useFlightOfferUi();

  const hasSearchResults =
    !embeddedFlightResultsEditor &&
    !forceLandingLayout &&
    (hasResults || (searchResults && searchResults.length > 0));
  const showMobileWizard =
    !embeddedFlightResultsEditor &&
    Boolean(isMobile && searchTab === PACKAGES);
  const showCampaignsPanel = searchTab === CAMPAIGNS;

  const isFlightOfferTab =
    searchTab === PLANES || searchTab === DYNAMIC_PACKAGES;
  const isSupportedFlightSearchTab =
    searchTab === PLANES || searchTab === DYNAMIC_PACKAGES;
  const isHotelRoute =
    searchTab === HOTELS ||
    searchTab === PACKAGES ||
    searchTab === ORGANIZED_TOURS ||
    searchTab === DYNAMIC_PACKAGES;
  const pathname = routerLocation?.pathname || "";
  const normalizedFlightStep = String(routeFlightStep || "").trim().toLowerCase();
  const inFlightWizardNonResultsStep =
    (pathname.includes("/flights/") ||
      normalizedFlightStep.length > 0) &&
    !pathname.includes("/flights/results") &&
    !pathname.includes("/flights/search") &&
    normalizedFlightStep !== "results" &&
    normalizedFlightStep !== "search";
  const shouldShowSearchLoadingModal =
    loading &&
    isHotelRoute &&
    searchTab !== PLANES &&
    !inFlightWizardNonResultsStep;
  const searchLoadingModalCaption =
    searchTab === PLANES
      ? "מחפשים עבורך את הטיסות הטובות ביותר..."
      : undefined;
  const showFlightCompactStrip =
    !embeddedFlightResultsEditor &&
    flightOfferCompactMode &&
    isFlightOfferTab &&
    !flightSearchExpanded;

  const passengersCount = (roomInfo?.rooms || []).reduce(
    (sum, room) =>
      sum +
      (Number(room?.adults) || 0) +
      (Number(room?.kids) || 0) +
      (Number(room?.infants) || 0),
    0
  );
  const hasDestination = Array.isArray(location)
    ? location.length > 0
    : Boolean(location);
  const hasDepartureDate = Boolean(calendarDateArrival);
  const needsReturnDate = flightTripType !== "OneWay";
  const hasReturnDate = Boolean(calendarDateLeave);
  const hasValidFlightSearchState =
    hasDestination &&
    hasDepartureDate &&
    (!needsReturnDate || hasReturnDate) &&
    passengersCount > 0;
  const shouldShowCompact =
    showFlightCompactStrip &&
    isSupportedFlightSearchTab &&
    hasValidFlightSearchState;
  const shouldShowFlightLoadingOnly =
    !embeddedFlightResultsEditor &&
    flightOfferCompactMode &&
    isSupportedFlightSearchTab &&
    loading &&
    !flightSearchExpanded &&
    !shouldShowSearchLoadingModal;
  const shouldShowInvalidFlightState =
    !embeddedFlightResultsEditor &&
    flightOfferCompactMode &&
    isSupportedFlightSearchTab &&
    !loading &&
    !hasValidFlightSearchState &&
    !flightSearchExpanded;
  const embeddedHasSearchSeed =
    hasDestination || hasDepartureDate || hasReturnDate || passengersCount > 0;
  const canRenderEmbeddedFlightEditor =
    isSupportedFlightSearchTab &&
    (loading || hasValidFlightSearchState || embeddedHasSearchSeed || embeddedEditorForceOpen);
  const showInternalSearchTypeBlock =
    !isMobile &&
    !(
      !embeddedFlightResultsEditor &&
      flightOfferCompactMode &&
      isSupportedFlightSearchTab
    );

  useEffect(() => {
    if (!loading) return;
    if (
      searchTab === HOTELS ||
      searchTab === PACKAGES ||
      searchTab === ORGANIZED_TOURS ||
      searchTab === DYNAMIC_PACKAGES ||
      searchTab === PLANES
    ) {
      return;
    }
    forceStopLoading();
  }, [loading, searchTab, forceStopLoading]);

  useEffect(() => {
    if (!loading) return;
    if (searchTab !== PACKAGES && searchTab !== HOTELS) return;
    const timer = window.setTimeout(() => {
      forceStopLoading();
    }, 90000);
    return () => window.clearTimeout(timer);
  }, [loading, searchTab, forceStopLoading]);

  useEffect(() => {
    if (loading) setFlightSearchExpanded(false);
  }, [loading]);

  useEffect(() => {
    if (!flightOfferCompactMode || !isFlightOfferTab) return;
    console.log("[SearchBlockContainer] flightOfferCompactSummary props", {
      flightOfferCompactMode,
      flightSearchExpanded,
      showFlightCompactStrip,
      searchTab,
    });
  }, [
    flightOfferCompactMode,
    isFlightOfferTab,
    flightSearchExpanded,
    showFlightCompactStrip,
    searchTab,
  ]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[SEARCH_EDIT_LAYOUT_DEBUG]", {
      route: routerLocation?.pathname || "",
      isEditMode:
        embeddedFlightResultsEditor || (flightOfferCompactMode && flightSearchExpanded),
      activeProductTab: searchTab,
      expandedSections: {
        embeddedFlightResultsEditor,
        flightSearchExpanded,
        embeddedEditorForceOpen,
      },
      affectedComponents: [
        "SearchBlockContainer",
        "SearchTypeBlock",
        "SearchFieldsBlock",
        "QuickSearchBlock",
      ],
    });
  }, [
    routerLocation?.pathname,
    embeddedFlightResultsEditor,
    flightOfferCompactMode,
    flightSearchExpanded,
    embeddedEditorForceOpen,
    searchTab,
  ]);

  useLayoutEffect(() => {
    if (!import.meta.env.DEV || !embeddedFlightResultsEditor) return;
    const root = embeddedPanelBodyRef.current;
    const fieldsMount = root?.querySelector?.(".search-fields-block");
    const typeMount = root?.querySelector?.(".search-type-block");
    console.log("[FLIGHT_SEARCH_PANEL_RENDER_STATE]", {
      route: routerLocation?.pathname || "",
      flightSearchExpanded,
      flightSearchEditorOpen,
      editorOpen: flightSearchEditorOpen,
      hasPanelChildren: Boolean(
        root && (root.childElementCount > 0 || (fieldsMount?.childElementCount ?? 0) > 0)
      ),
      panelMounted: Boolean(root),
      activeSearchTab: searchTab,
      embeddedCanRender: canRenderEmbeddedFlightEditor,
      searchTypeBlockMounted: Boolean(typeMount),
    });
  }, [
    embeddedFlightResultsEditor,
    routerLocation?.pathname,
    flightSearchExpanded,
    flightSearchEditorOpen,
    searchTab,
    canRenderEmbeddedFlightEditor,
  ]);

  if (loadingModalOnly) {
    return (
      <SearchLoadingModal
        open={shouldShowSearchLoadingModal}
        caption={searchLoadingModalCaption}
        enableFlightsDevLogging={searchTab === PLANES}
      />
    );
  }

  if (
    !embeddedFlightResultsEditor &&
    flightOfferCompactMode &&
    !isSupportedFlightSearchTab
  ) {
    return null;
  }

  return (
    <>
      <SearchLoadingModal
        open={shouldShowSearchLoadingModal}
        caption={searchLoadingModalCaption}
        enableFlightsDevLogging={searchTab === PLANES}
      />

      <div
        className={[
          "search-block-container",
          hasSearchResults ? "has-results" : "",
          embeddedFlightResultsEditor
            ? "search-block-container--embedded-flight-editor"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-search-tab={searchTab}
      >
        {embeddedFlightResultsEditor ? (
          canRenderEmbeddedFlightEditor ? (
            <div
              ref={embeddedPanelBodyRef}
              className="search-blocks-wrapper search-blocks-wrapper--embedded-flight"
            >
              {!isMobile ? (
                <div className="search-engine-desktop-nav search-block search-type-block">
                  <SearchTypeBlock />
                </div>
              ) : null}
              <div className="search-engine-desktop-card search-engine-desktop-card--embedded-flight-solo">
                <div className="search-engine-desktop-row">
                  <div className="search-block search-fields-block">
                    <SearchFieldsBlock
                      mobileSecondaryNav={
                        isMobile ? (
                          <MobileCategoryPicker variant="secondary" />
                        ) : null
                      }
                      onBeforeFlightResultsEditorSubmitSearch={
                        embeddedFlightResultsEditor
                          ? markFlightEditorSearchSubmittedFromResultsBar
                          : null
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              ref={embeddedPanelBodyRef}
              className="search-blocks-wrapper search-blocks-wrapper--embedded-flight search-blocks-wrapper--embedded-flight-fields-only"
            >
              <div className="search-engine-desktop-card search-engine-desktop-card--embedded-flight-solo">
                <div className="search-engine-desktop-row" dir="rtl">
                  <div className="search-block search-fields-block">
                    <div className="flight-search-empty-state">
                      <p className="flight-search-empty-state__text">
                        לא ניתן לפתוח עריכת חיפוש בלי נתוני טיסה בסיסיים
                      </p>
                      <button
                        type="button"
                        className="flight-search-empty-state__cta"
                        onClick={() => {
                          if (!isSupportedFlightSearchTab) {
                            setSearchTab(PLANES, { source: "user_click" });
                          }
                          setEmbeddedEditorForceOpen(true);
                        }}
                      >
                        התחל חיפוש חדש
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : showMobileWizard ? (
          <MobileSearchWizard onCloseSearchEditor={onCloseSearchEditor} />
        ) : (
          <>
          <div
            className={[
              "search-blocks-wrapper",
              showCampaignsPanel ? "search-blocks-wrapper--campaigns-tabs-only" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {showInternalSearchTypeBlock && (
              <div
                className={[
                  "search-engine-desktop-nav search-block search-type-block",
                  onCloseSearchEditor ? "search-type-block--with-close" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <SearchTypeBlock />
                {onCloseSearchEditor ? (
                  <button
                    type="button"
                    className="search-engine-inline-close-btn"
                    onClick={onCloseSearchEditor}
                  >
                    סגור
                  </button>
                ) : null}
              </div>
            )}
            {showCampaignsPanel ? (
              isMobile ? (
                <div className="search-engine-desktop-card search-engine-desktop-card--campaigns-mobile">
                  <MobileCategoryPicker variant="secondary" />
                </div>
              ) : null
            ) : (
              <div className="search-engine-desktop-card">
                {shouldShowFlightLoadingOnly ? (
                  <div className="search-engine-desktop-row" dir="rtl">
                    <Skeleton active paragraph={{ rows: 2 }} title={{ inlineSize: "35%" }} />
                  </div>
                ) : shouldShowInvalidFlightState ? (
                  <div className="search-engine-desktop-row" dir="rtl">
                    <div className="search-block search-fields-block">
                      <div className="flight-search-empty-state">
                        <p className="flight-search-empty-state__text">
                          נתוני החיפוש אינם תקינים כרגע
                        </p>
                        <button
                          type="button"
                          className="flight-search-empty-state__cta"
                          onClick={() => setFlightSearchExpanded(true)}
                        >
                          התחל חיפוש חדש
                        </button>
                      </div>
                    </div>
                  </div>
                ) : shouldShowCompact ? (
                  <FlightOfferCompactSummary
                    onEditSearch={() => setFlightSearchExpanded(true)}
                    editorOpen={flightSearchExpanded}
                  />
                ) : (
                  <div className="search-engine-desktop-row">
                    <div className="search-block search-fields-block">
                      <SearchFieldsBlock
                        mobileSecondaryNav={
                          isMobile ? (
                            <MobileCategoryPicker variant="secondary" />
                          ) : null
                        }
                        onCloseSearchEditor={
                          !showInternalSearchTypeBlock ? onCloseSearchEditor : null
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            {searchTab === PACKAGES && (
              <div className="search-engine-desktop-quickfilters search-block quick-search-block">
                <QuickSearchBlock />
              </div>
            )}
          </div>
          </>
        )}
      </div>
    </>
  );
};

export default SearchBlockContainer;

